import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createReview, getProductReviews } from '../../Store/reviews/reviewsSlice';
import { toggleForm, toggleFormType } from '../../Store/user/userSlice';
import styles from '../../styles/Product.module.scss';

const StarRating = ({ value, onChange, readOnly = false, size = 'md' }) => (
  <div className={`${styles.reviewStars} ${styles[`reviewStars_${size}`]} ${readOnly ? styles.reviewStarsReadonly : ''}`}>
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        className={star <= value ? styles.reviewStarActive : styles.reviewStar}
        onClick={() => !readOnly && onChange?.(star)}
        disabled={readOnly}
        aria-label={`${star} зірок`}
      >
        ★
      </button>
    ))}
  </div>
);

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('uk-UA', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const ProductReviews = ({ productId }) => {
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state) => state.user);
  const bucket = useSelector((state) => state.reviews.byProduct[productId]);
  const { isLoading } = useSelector((state) => state.reviews);

  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const reviews = bucket?.reviews || [];
  const count = bucket?.count || 0;
  const averageRating = bucket?.averageRating || 0;
  const userReview = reviews.find((r) => r.User?.id === currentUser?.id);

  useEffect(() => {
    if (productId) {
      dispatch(getProductReviews(productId));
    }
  }, [dispatch, productId]);

  const openLogin = () => {
    dispatch(toggleFormType('login'));
    dispatch(toggleForm(true));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      openLogin();
      return;
    }
    if (!text.trim()) {
      setError('Напишіть текст відгуку');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await dispatch(createReview({ productId, rating, text: text.trim() })).unwrap();
      setText('');
      setRating(5);
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Не вдалося надіслати відгук');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.section}>
      <div className={`${styles.sectionHeader} ${styles.reviewSectionHeader}`}>
        Відгуки
        {count > 0 && (
          <span className={styles.reviewSummary}>
            <StarRating value={Math.round(averageRating)} readOnly size="sm" />
            <span>{averageRating} · {count} відгук{count === 1 ? '' : count < 5 ? 'и' : 'ів'}</span>
          </span>
        )}
      </div>
      <div className={styles.sectionBody}>
        {!currentUser ? (
          <div className={styles.reviewLoginPrompt}>
            <button type="button" onClick={openLogin}>Увійдіть</button>
            <span>, щоб залишити відгук</span>
          </div>
        ) : userReview ? (
          <p className={styles.reviewAlready}>Ви вже залишили відгук на цей товар.</p>
        ) : (
          <form className={styles.reviewForm} onSubmit={handleSubmit}>
            <div className={styles.reviewFormRow}>
              <span className={styles.reviewFormLabel}>Ваша оцінка</span>
              <StarRating value={rating} onChange={setRating} />
            </div>
            <textarea
              className={styles.reviewTextarea}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Розкажіть про товар: якість, доставку, враження..."
              rows={4}
              maxLength={2000}
            />
            {error && <p className={styles.reviewError}>{error}</p>}
            <button type="submit" className={styles.reviewSubmit} disabled={submitting}>
              {submitting ? 'Надсилання…' : 'Надіслати відгук'}
            </button>
          </form>
        )}

        {isLoading && !reviews.length ? (
          <p className={styles.reviewEmpty}>Завантаження відгуків…</p>
        ) : reviews.length === 0 ? (
          <p className={styles.reviewEmpty}>Поки немає відгуків. Будьте першим!</p>
        ) : (
          <ul className={styles.reviewList}>
            {reviews.map((review) => (
              <li key={review.id} className={styles.reviewItem}>
                <div className={styles.reviewItemHead}>
                  <strong>{review.User?.name || 'Покупець'}</strong>
                  <StarRating value={review.rating} readOnly size="sm" />
                  <span className={styles.reviewDate}>{formatDate(review.createdAt)}</span>
                </div>
                <p className={styles.reviewText}>{review.text}</p>
                {review.adminReply && (
                  <div className={styles.reviewReply}>
                    <strong>Відповідь магазину</strong>
                    <p>{review.adminReply}</p>
                    {review.adminReplyAt && (
                      <span className={styles.reviewDate}>{formatDate(review.adminReplyAt)}</span>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ProductReviews;
